import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Heart, ArrowLeft, Bookmark, ChevronUp, Volume2, VolumeX } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import FavouriteButton from '../components/FavouriteButton';
import { useGuestIdentity } from '../utils/useGuestIdentity';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SEEN_KEY = 'crystal_inspo_seen_ids';
const SAVED_TAGS_KEY = 'crystal_inspo_saved_tags';
const SAVED_TYPES_KEY = 'crystal_inspo_saved_types';

const readLS = (k, fallback) => {
  try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
};
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };

/**
 * /inspo — TikTok-style vertical feed.
 *
 * One full-viewport slide per item. Scroll-snap, autoplay-on-visible video,
 * infinite paging. Algorithm: server ranks by popularity + recency + tag/type
 * boosts inferred client-side from saved-history (tracked in localStorage).
 *
 * Saves piggy-back on FavouriteButton + the global SaveToastHost mounted in App.js.
 */
const InspoPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [muted, setMuted] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const { faves } = useGuestIdentity();
  const containerRef = useRef(null);
  const slideRefs = useRef({});
  const pendingImpressionsRef = useRef([]);
  const fetchedSeedRef = useRef(false);

  // Recompute saved tags + media types from faves whenever they change
  const computeBoosts = useCallback(() => {
    try {
      const savedTags = readLS(SAVED_TAGS_KEY, []);
      const savedTypes = readLS(SAVED_TYPES_KEY, []);
      // Top 5 most-recent tags, top 2 media types
      return { tags: savedTags.slice(0, 5), types: savedTypes.slice(0, 2) };
    } catch { return { tags: [], types: [] }; }
  }, []);

  // Fetch next page from /inspo/feed
  const fetchMore = useCallback(async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    const seen = readLS(SEEN_KEY, []);
    const { tags, types } = computeBoosts();
    try {
      const res = await fetch(`${API_URL}/api/inspo/feed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          exclude_ids: seen.slice(-200), // last 200 only — keep payload small
          boost_tags: tags,
          boost_media_types: types,
          limit: 8,
        }),
      });
      const data = await res.json();
      const next = data.items || [];
      if (next.length === 0) {
        setHasMore(false);
      } else {
        setItems((prev) => [...prev, ...next]);
        // Remember new ids in seen list (cap at 500)
        const newSeen = [...seen, ...next.map(i => i.id)].slice(-500);
        writeLS(SEEN_KEY, newSeen);
      }
      setHasMore(data.has_more && next.length > 0);
    } catch (e) {
      console.error('Feed fetch failed', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [computeBoosts, loadingMore]);

  // Initial load — only once
  useEffect(() => {
    if (fetchedSeedRef.current) return;
    fetchedSeedRef.current = true;
    fetchMore();
  }, [fetchMore]);

  // When a save happens, capture the saved item's tags + media_type so the
  // next feed call boosts similar items.
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      if (!d.nowFav || d.itemType !== 'inspo') return;
      const item = items.find(i => i.id === d.itemId);
      if (!item) return;
      const prevTags = readLS(SAVED_TAGS_KEY, []);
      const newTags = [...(item.tags || []), ...prevTags].slice(0, 50);
      writeLS(SAVED_TAGS_KEY, newTags);
      const prevTypes = readLS(SAVED_TYPES_KEY, []);
      const newTypes = [item.media_type, ...prevTypes].slice(0, 20);
      writeLS(SAVED_TYPES_KEY, newTypes);
    };
    window.addEventListener('crystal-save', handler);
    return () => window.removeEventListener('crystal-save', handler);
  }, [items]);

  // IntersectionObserver: track which slide is "active" and autoplay-only-visible video,
  // fire impressions, prefetch when nearing the end.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const idx = parseInt(entry.target.getAttribute('data-idx') || '0', 10);
        const itemId = entry.target.getAttribute('data-item-id');
        const videoEl = entry.target.querySelector('video');
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          setActiveIdx(idx);
          if (videoEl) { videoEl.play().catch(() => {}); }
          // Queue impression for batched POST
          if (itemId) pendingImpressionsRef.current.push(itemId);
          // Prefetch when near end (within 3 of last)
          if (idx >= items.length - 3 && hasMore && !loadingMore) {
            fetchMore();
          }
        } else if (videoEl) {
          try { videoEl.pause(); } catch { /* ignore */ }
        }
      });
    }, { root, threshold: [0, 0.6, 0.9] });

    Object.values(slideRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [items, hasMore, loadingMore, fetchMore]);

  // Flush impressions in batches every 3s
  useEffect(() => {
    const flush = async () => {
      const batch = Array.from(new Set(pendingImpressionsRef.current));
      pendingImpressionsRef.current = [];
      if (batch.length === 0) return;
      try {
        await fetch(`${API_URL}/api/inspo/track-impressions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_ids: batch }),
        });
      } catch (e) { /* fire-and-forget */ }
    };
    const t = setInterval(flush, 3000);
    return () => { clearInterval(t); flush(); };
  }, []);

  // Snap-scroll to next/prev slide with keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === ' ') {
        e.preventDefault();
        const target = slideRefs.current[activeIdx + 1];
        target && target.scrollIntoView({ behavior: 'smooth' });
      }
      if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        const target = slideRefs.current[activeIdx - 1];
        target && target.scrollIntoView({ behavior: 'smooth' });
      }
      if (e.key === 'm' || e.key === 'M') {
        setMuted(m => !m);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeIdx]);

  const totalSaved = (faves || []).length;

  return (
    <div className="bg-black text-white min-h-screen" data-testid="inspo-page" style={{ touchAction: 'pan-y' }}>
      <SEOHead
        title="Wedding Inspo — Crystal Media"
        description="A vertical feed of wedding stills, clips, and moments. Save inspiration to your own boards."
        url="/inspo"
      />

      {/* Floating top chrome (always visible over feed) */}
      <div className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3"
           style={{ paddingTop: 'calc(12px + env(safe-area-inset-top, 0px))', background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)' }}>
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-white/85 hover:text-white tap-target" data-testid="inspo-back">
          <ArrowLeft size={14} /> Home
        </Link>
        <p className="font-presti text-base tracking-[0.15em] uppercase">Inspo</p>
        <Link to="/inspo/saved" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-white/85 hover:text-white tap-target" data-testid="inspo-saved-link">
          <Bookmark size={13} fill={totalSaved > 0 ? 'currentColor' : 'none'} />
          {totalSaved > 0 ? totalSaved : ''}
        </Link>
      </div>

      {/* The feed */}
      <div
        ref={containerRef}
        className="overflow-y-auto snap-y snap-mandatory scroll-smooth"
        style={{ height: '100svh', minHeight: '100vh', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        data-testid="inspo-feed"
      >
        <style>{`
          [data-testid="inspo-feed"]::-webkit-scrollbar { display: none; }
        `}</style>

        {loading ? (
          <div className="h-screen flex items-center justify-center">
            <Loader2 className="animate-spin text-white/40" size={28} />
          </div>
        ) : items.length === 0 ? (
          <div className="h-screen flex flex-col items-center justify-center px-6 text-center">
            <Heart size={32} className="text-white/30 mb-4" />
            <p className="text-white/70 text-sm mb-2">No inspo yet.</p>
            <p className="text-white/40 text-xs max-w-xs">Check back soon — we&rsquo;re curating fresh moments daily.</p>
          </div>
        ) : (
          items.map((item, i) => (
            <FeedSlide
              key={item.id}
              ref={(el) => { slideRefs.current[i] = el; }}
              item={item}
              idx={i}
              muted={muted}
              onToggleMute={() => setMuted(m => !m)}
              isLast={i === items.length - 1 && !hasMore}
            />
          ))
        )}

        {loadingMore && items.length > 0 && (
          <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-white/30" size={20} /></div>
        )}
      </div>

      {/* First-visit nudge to scroll — fades after first interaction */}
      {!loading && items.length > 1 && activeIdx === 0 && (
        <div className="fixed left-1/2 -translate-x-1/2 z-30 pointer-events-none flex flex-col items-center gap-1 text-white/65 text-[10px] tracking-[0.25em] uppercase"
             style={{ bottom: 'calc(28px + env(safe-area-inset-bottom, 0px))', animation: 'scrollNudge 1.6s ease-in-out infinite' }}>
          <ChevronUp size={16} />
          <span>Swipe up</span>
          <style>{`@keyframes scrollNudge{0%,100%{transform:translate(-50%,0);opacity:.45}50%{transform:translate(-50%,-6px);opacity:.85}}`}</style>
        </div>
      )}
    </div>
  );
};

/* ─────────────── Feed slide ─────────────── */
const FeedSlide = React.forwardRef(({ item, idx, muted, onToggleMute, isLast }, ref) => {
  const isVideo = item.media_type === 'video';
  const kind = item.kind || 'inspo'; // 'inspo' | 'photo' | 'film'

  // Where to deep-link when the user wants to see the source page
  const sourceLink = (() => {
    if (kind === 'photo' && item.collection_id) return `/collection/${item.collection_id}`;
    if (kind === 'film') return '/film/theatre';
    return null;
  })();

  return (
    <section
      ref={ref}
      data-idx={idx}
      data-item-id={item.id}
      data-testid={`inspo-slide-${item.id}`}
      className="snap-start relative w-full overflow-hidden flex items-center justify-center bg-black"
      style={{ height: '100svh', minHeight: '100vh' }}
    >
      {/* Media — fits to viewport, letterboxed on portrait imagery */}
      {isVideo ? (
        <video
          src={item.media_url}
          poster={item.thumbnail_url || undefined}
          className="absolute inset-0 w-full h-full object-contain"
          loop
          muted={muted}
          playsInline
          preload="metadata"
        />
      ) : (
        <img
          src={item.media_url}
          alt=""
          className="absolute inset-0 w-full h-full object-contain"
          loading={idx > 1 ? 'lazy' : 'eager'}
        />
      )}

      {/* Soft gradient overlay for legibility */}
      <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/75 via-black/30 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/45 to-transparent pointer-events-none" />

      {/* Right-side action stack — vertical, with extra clearance for mobile browser chrome */}
      <div className="absolute right-3 sm:right-5 z-20 flex flex-col items-center gap-5"
           style={{ bottom: 'calc(32px + env(safe-area-inset-bottom, 0px) + var(--inspo-chrome-pad, 56px))' }}>
        <FavouriteButton itemType={kind} itemId={item.id} variant="ghost" size={26} />
        {isVideo && (
          <button
            type="button"
            onClick={onToggleMute}
            data-testid={`inspo-slide-mute-${item.id}`}
            className="w-10 h-10 inline-flex items-center justify-center text-white/85 hover:text-white tap-target"
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
        )}
      </div>

      {/* Bottom caption — also lifted above mobile chrome */}
      {(item.caption || sourceLink) && (
        <div className="absolute inset-x-0 z-10 px-5 sm:px-7"
             style={{ bottom: 'calc(20px + env(safe-area-inset-bottom, 0px) + var(--inspo-chrome-pad, 40px))' }}>
          {item.caption && (
            <p className="font-presti text-white text-lg sm:text-xl leading-snug max-w-md drop-shadow-md">{item.caption}</p>
          )}
          <div className="mt-2 flex items-center gap-3 flex-wrap text-[10px] tracking-[0.2em] uppercase text-white/55">
            {kind === 'photo' && <span className="px-2 py-0.5 border border-white/30 text-white/80">Gallery</span>}
            {kind === 'film' && <span className="px-2 py-0.5 border border-white/30 text-white/80">Film</span>}
            {item.tags && item.tags.slice(0, 3).map(t => (
              <span key={t}>#{t}</span>
            ))}
            {sourceLink && (
              <Link to={sourceLink} className="ml-auto underline-offset-2 hover:underline" data-testid={`inspo-slide-source-${item.id}`}>
                View source →
              </Link>
            )}
          </div>
        </div>
      )}

      {/* End-of-feed prompt on the last slide */}
      {isLast && (
        <div className="absolute inset-x-0 bottom-0 z-30 px-6 pb-10 pt-20 text-center bg-gradient-to-t from-black via-black/90 to-transparent">
          <p className="text-white/55 text-xs tracking-[0.25em] uppercase mb-3">You&rsquo;re caught up</p>
          <Link to="/inspo/saved" data-testid="inspo-end-saved" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-black text-[10px] tracking-[0.2em] uppercase font-medium hover:bg-white/85 transition-colors">
            <Bookmark size={13} /> View saved
          </Link>
        </div>
      )}
    </section>
  );
});

export default InspoPage;
